import { Trans, t } from '@lingui/macro';
import { useState, useReducer, useEffect } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { X, Copy, Save, Trash2, Download, Calendar, Lock, Hash, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { Room } from "../../models/Room";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { ExternalShareLink, ExternalShareLinkDownload } from "../../models/ExternalShareLink";
import { RoomItem } from '../../models/RoomItem';
import { FileShareItem } from '../components/ItemFilePopover';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { Clipboard } from '@capacitor/clipboard';

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Progress } from "../../components/ui/progress";
import { cn } from "../../lib/utils";

export const ShareLinkModal: React.FC<{
  show: boolean,
  isNew: boolean,
  setShow: (v: boolean) => void,
  link: ExternalShareLink,
  room: Room,
  downloads: ExternalShareLinkDownload[],
  items: RoomItem[],
  onDone: () => void
}> = ({ show, isNew, setShow, link, room, downloads, items, onDone }) => {
  const { toast } = useToast();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [selectedOriginal, setSelectedOriginal] = useState<ExternalShareLink>();
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [isExpired, setIsExpired] = useState(link.expireDate ? true : false);
  const [isDownloads, setIsDownloads] = useState(link.maxDownloads ? true : false);
  const [isPassword, setIsPassword] = useState(link.password ? true : false);
  const timezoneSeconds = -new Date().getTimezoneOffset() * 60;

  // Alert dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDownloadsDialog, setShowDownloadsDialog] = useState(false);
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isShowUrl, setIsShowUrl] = useState(false);

  useEffect(() => {
    setSelectedOriginal(Util.pojo(link));
  }, []);

  function copyToClipboard() {
    const text = Util.getPublicSecureShareURL(link.id);
    const doCopy = () => Clipboard.write({ string: text }).then(() => {
      toast({
        title: t`Copied to clipboard`,
        duration: 2000,
      });
    });

    if (!Util.hasSameData(link, selectedOriginal)) {
      setShowUnsavedDialog(true);
      return;
    }
    doCopy();
  }

  async function handleSaveAndCopy() {
    setShowUnsavedDialog(false);
    await saveOnly();
    const text = Util.getPublicSecureShareURL(link.id);
    await Clipboard.write({ string: text });
    toast({
      title: t`Saved and copied to clipboard`,
      duration: 2000,
    });
  }

  function saveOnly(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (Util.hasSameData(link, selectedOriginal)) {
        resolve();
        return;
      }
      setSavingInProgress(true);
      link.user = My.user;
      link.updated = new Date();
      Fire.item.saveExternalShareLink(link).then(() => {
        setSelectedOriginal(Util.pojo(link));
        setSavingInProgress(false);
        onDone();
        resolve();
      }).catch((e) => {
        setSavingInProgress(false);
        console.error('Failed to save share link', e);
        toast({
          title: t`Failed to create invitation link`,
          duration: 1500,
        });
        reject(e);
      });
    });
  }

  async function saveChanges() {
    if (Util.hasSameData(link, selectedOriginal)) {
      toast({
        title: t`No changes detected`,
        duration: 1000,
      });
    } else {
      setSavingInProgress(true);
      link.user = My.user;
      link.updated = new Date();
      await Fire.item.saveExternalShareLink(link);
      setSelectedOriginal(Util.pojo(link));
      setSavingInProgress(false);
      onDone();
      setShowSaveSuccessDialog(true);
    }
  }

  async function deleteLink() {
    if (link) {
      await Fire.item.deleteExternalShareLink(link);
      setShow(false);
    }
  }

  function showDownloadsInfo() {
    setShowDownloadsDialog(true);
  }

  function handleExpireDateChange(value: string) {
    if (value) {
      link.expireDate = new Date(value);
    }
  }

  function handleToggleExpired(checked: boolean) {
    setIsExpired(checked);
    if (checked && !link.expireDate) {
      link.expireDate = new Date(new Date().setHours(0, 0, 0, 0) + (7 * 24 * 60 * 60 + timezoneSeconds) * 1000);
    } else if (!checked) {
      link.expireDate = null;
    }
  }

  function handleToggleDownloads(checked: boolean) {
    setIsDownloads(checked);
    if (!checked) {
      link.maxDownloads = null;
    }
  }

  function handleTogglePassword(checked: boolean) {
    setIsPassword(checked);
    if (!checked) {
      link.password = null;
    }
  }

  const linkUrl = Util.getPublicSecureShareURL(link.id);
  console.log(linkUrl);
  const hasUnsavedChanges = !Util.hasSameData(link, selectedOriginal);

  const getDefaultExpireDate = () => {
    if (link.expireDate) {
      return new Date(new Date(link.expireDate).getTime() + timezoneSeconds * 1000).toISOString().slice(0, 16);
    }
    return new Date(new Date().setHours(0, 0, 0, 0) + (7 * 24 * 60 * 60 + timezoneSeconds) * 1000).toISOString().slice(0, 16);
  };

  return (
    <>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-lg h-[85vh] md:h-auto md:max-h-[90vh] flex flex-col p-0 overflow-hidden">
          {/* 헤더 - 고정 */}
          <div className="p-6 pb-2">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>
                  {isNew ? <Trans>Create Share Link</Trans> : <Trans>Share links</Trans>}
                </DialogTitle>
                <div className="text-right text-xs text-gray-500 mr-6">
                  <span className="font-medium text-orange-600">{link.user.name}</span>
                  <div>{UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}</div>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* 프로그레스 바 */}
          {inProgress && <Progress className="mx-6 h-1" />}

          {/* 본문 - 스크롤 가능 */}
          <div className="flex-1 overflow-y-auto px-6 py-2 space-y-6">
            {/* 링크 URL 복사 영역 */}
            <div
              onClick={() => setIsShowUrl(!isShowUrl)}
              className="group bg-orange-100/30 p-3 rounded-md text-xs sm:text-sm text-gray-600 cursor-pointer border border-orange-200 hover:border-orange-300 transition-all w-full overflow-hidden flex items-center justify-between gap-2"
            >
              <span className={`flex-1 min-w-0 ${!isShowUrl ? 'truncate' : 'break-all whitespace-normal'}`}>
                {linkUrl}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <div className="text-gray-400 group-hover:text-orange-500 transition-colors">
                  {isShowUrl ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 gap-1 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-200/50"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard();
                  }}
                >
                  <Copy className="w-3.5 h-3.5" />
                  <Trans>Copy</Trans>
                </Button>
              </div>
            </div>

            {/* 파일 목록 */}
            <div className="space-y-2">
              <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-xl">
                {link.roomItems.map(item => (
                  <FileShareItem
                    key={item.id}
                    item={item}
                    selectedItems={link.roomItems}
                    downloads={downloads.filter(d => d.itemId == item.id)}
                    checkable={false}
                    remove={() => forceUpdate()}
                  />
                ))}
                {link.roomItems.length === 0 && (
                  <p className="text-sm text-red-500 text-center py-2">
                    <Trans>No files added.</Trans>
                  </p>
                )}
              </div>
              {downloads.length > 0 && (
                <button
                  onClick={showDownloadsInfo}
                  className="text-sm text-orange-600 hover:text-orange-700 hover:underline"
                >
                  <Trans>{downloads.length} downloads</Trans>
                </button>
              )}
            </div>

            {/* 설정 필드 */}
            <div className="space-y-4">
              {/* 제목 */}
              <div className="flex items-center justify-between gap-4">
                <Label className="shrink-0 text-gray-700">{t`Title`}</Label>
                <Input
                  placeholder={t`Optional title`}
                  value={link.title || ''}
                  onChange={(e) => { link.title = e.target.value; forceUpdate(); }}
                  maxLength={35}
                  className="max-w-[240px]"
                />
              </div>

              {/* 만료일 */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <Label className="text-gray-700">{t`Expire date`}</Label>
                </div>
                <div className="flex items-center gap-2">
                  {isExpired && (
                    <Input
                      type="datetime-local"
                      value={getDefaultExpireDate()}
                      onChange={(e) => handleExpireDateChange(e.target.value)}
                      className="w-auto text-sm"
                    />
                  )}
                  <Switch
                    checked={isExpired}
                    onCheckedChange={handleToggleExpired}
                  />
                </div>
              </div>

              {/* 최대 다운로드 수 */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-gray-500" />
                  <Label className="text-gray-700 text-sm">{t`Max downloads per file`}</Label>
                </div>
                <div className="flex items-center gap-2">
                  {isDownloads && (
                    <Input
                      type="number"
                      placeholder={t`Number`}
                      value={link.maxDownloads || ''}
                      onChange={(e) => { link.maxDownloads = e.target.value ? parseInt(e.target.value) : null; forceUpdate(); }}
                      className="w-20 text-sm"
                    />
                  )}
                  <Switch
                    checked={isDownloads}
                    onCheckedChange={handleToggleDownloads}
                  />
                </div>
              </div>

              {/* 비밀번호 */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <Label className="text-gray-700">{t`Password`}</Label>
                </div>
                <div className="flex items-center gap-2">
                  {isPassword && (
                    <Input
                      type="text"
                      placeholder={t`Optional password`}
                      value={link.password || ''}
                      onChange={(e) => { link.password = e.target.value; forceUpdate(); }}
                      className="w-32 text-sm"
                    />
                  )}
                  <Switch
                    checked={isPassword}
                    onCheckedChange={handleTogglePassword}
                  />
                </div>
              </div>
            </div>

          </div>
          {/* 푸터 (버튼 영역) - 고정 */}
          <div className="p-6 pt-4 border-t bg-gray-50/50">
            {savingInProgress && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
              </div>
            )}
            {!savingInProgress && (
              <div className="space-y-3">
                {hasUnsavedChanges && (
                  <p className="text-[10px] text-center text-gray-400">
                    <Trans>Remember to save your changes.</Trans>
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={saveChanges}
                    className="flex-1 gap-2 bg-orange-500 hover:bg-orange-600 shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    {isNew ? <Trans>Save</Trans> : <Trans>Save changes</Trans>}
                  </Button>
                  {!isNew && (
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(true)}
                      className="gap-2 border-solid border-gray-200 text-red-500 hover:bg-red-50 hover:text-red-600 shadow-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      <Trans>Delete</Trans>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete link`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Are you sure you want to delete this link?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLink} className="bg-red-500 hover:bg-red-600">
              {t`Delete`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 다운로드 정보 다이얼로그 */}
      <AlertDialog open={showDownloadsDialog} onOpenChange={setShowDownloadsDialog}>
        <AlertDialogContent className="max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Recent downloads`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                {downloads.length > 0 ? (
                  downloads.map((d, index) => {
                    const user = d.userId ? My.getUserFromRooms(d.userId)?.name ?? t`UserId: ${d.userId}` : t`Anonymous (ip: ${d.ip})`;
                    return (
                      <div key={index} className="p-2 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-900">{d.filename}</p>
                        <p className="text-gray-500">{user}, {UtilApp.dateStrToTimeAndDay(d.created, My.user.lang)}</p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500">{t`No downloads yet`}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>{t`OK`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 저장 성공 다이얼로그 */}
      <AlertDialog open={showSaveSuccessDialog} onOpenChange={setShowSaveSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Successfully saved`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Would you like to copy the link to clipboard?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Close`}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const text = Util.getPublicSecureShareURL(link.id);
                Clipboard.write({ string: text });
                toast({ title: t`Copied to clipboard`, duration: 2000 });
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {t`Copy`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 저장되지 않은 변경사항 다이얼로그 */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Unsaved changes`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`There are unsaved changes. Save and copy the link?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndCopy} className="bg-orange-500 hover:bg-orange-600">
              {t`Save & Copy`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ShareLinkModal;